import React, { useEffect, useMemo, useState } from "react";
import { Check, X } from "lucide-react";

const SHAPE_LABEL = { cube: "Cube", sphere: "Sphere", torus: "Torus", cone: "Cone", cylinder: "Cylinder", monkey: "Monkey head" };

function Segment({ label, items, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {items.map((it) => (
          <button key={it.id} type="button" disabled={!it.enabled} onClick={() => onChange(it.id)} data-testid={`${testid}-${it.id}`}
            title={it.enabled ? undefined : "Coming in a later phase"}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value === it.id ? "bg-white text-black border-white"
              : it.enabled ? "border-white/15 text-slate-200 hover:border-white/40" : "border-white/5 text-slate-500 cursor-not-allowed"}`}>
            {it.label}{!it.enabled && <span className="ml-1 text-[9px] uppercase tracking-wider">soon</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

const minutes = (s) => (s < 90 ? `${Math.round(s)} sec` : `${Math.round(s / 60)} min`);

export function ApprovalPanel({ job, options, shapes, balance, busy, onApprove, onCancel }) {
  const [mode, setMode] = useState(job.settings.mode);
  const [duration, setDuration] = useState(job.settings.duration);
  const [fps, setFps] = useState(job.settings.fps);
  const [shape, setShape] = useState(job.shape);
  const modeCfg = options.modes.find((m) => m.id === mode);
  useEffect(() => { if (!modeCfg.fps.includes(fps)) setFps(modeCfg.fps[0]); }, [modeCfg, fps]);
  const credits = Number(options.credits?.[mode]?.[String(duration)] || 0);
  const frames = duration * fps;
  const same = mode === job.settings.mode && duration === job.settings.duration && fps === job.settings.fps;
  const eta = same && job.estimate?.render_seconds ? `About ${minutes(job.estimate.render_seconds)}` : "Measured on this server after the first render";
  const short = balance != null && credits > balance;
  const modes = useMemo(() => options.modes.map((m) => ({ id: m.id, label: `${m.label} · ${m.resolution}`, enabled: m.enabled })), [options]);
  return (
    <div className="space-y-4" data-testid="render3d-approval">
      {shapes.length > 0 && (
        <Segment label="Shape" testid="render3d-shape" value={shape} onChange={setShape}
          items={shapes.map((s) => ({ id: s, label: SHAPE_LABEL[s] || s, enabled: true }))} />
      )}
      <Segment label="Quality" testid="render3d-mode" value={mode} onChange={setMode} items={modes} />
      <div className="flex flex-wrap gap-5">
        <Segment label="Length" testid="render3d-duration" value={duration} onChange={setDuration}
          items={options.durations.map((d) => ({ id: d.seconds, label: `${d.seconds}s`, enabled: d.enabled }))} />
        <Segment label="Frame rate" testid="render3d-fps" value={fps} onChange={setFps}
          items={[30, 60].map((f) => ({ id: f, label: `${f} FPS`, enabled: modeCfg.enabled && modeCfg.fps.includes(f) }))} />
      </div>
      <div className="rounded-xl border border-white/10 bg-black/40 px-3.5 py-3 text-xs text-slate-400 space-y-1" data-testid="render3d-estimate">
        <div className="text-slate-200">{modeCfg.label} · {modeCfg.engine === "CYCLES" ? "Cycles" : "Eevee"} · {modeCfg.resolution} · {fps} FPS · {frames.toLocaleString()} frames</div>
        <div>Price: <span className="text-white" data-testid="render3d-credits">{credits.toLocaleString()} credits</span>{credits === 0 && " (free while being tested)"}</div>
        <div>Render time: <span data-testid="render3d-eta">{eta}</span></div>
        {short && <div className="text-red-300" data-testid="render3d-short">You need {credits - balance} more credits.</div>}
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={busy || short} onClick={() => onApprove({ mode, duration, fps, shape })} data-testid="render3d-approve"
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-black text-xs font-medium hover:bg-slate-200 disabled:opacity-40 transition-colors">
          <Check className="w-3.5 h-3.5" />Approve and render
        </button>
        <button type="button" disabled={busy} onClick={onCancel} data-testid="render3d-decline"
          className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-xs text-slate-300 hover:bg-white/10 transition-colors">
          <X className="w-3.5 h-3.5" />Not now
        </button>
      </div>
    </div>
  );
}
